import React, { useEffect, useState } from "react";
import { AlertTriangle, KeyRound, Save, ShieldCheck, UploadCloud } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { useOperador } from "../../Hooks/useOperador";
import { operadorEhAdminOuGerente } from "../../Utils/cargos";
import { assinaturaDigitalService } from "../../Services/assinaturaDigital";
import "./styles.css";

const formInicial = {
  certificado: null,
  senha: "",
  certificadoTitular: "",
  certificadoCnpj: "",
  certificadoSerial: "",
  validadeCertificado: "",
  observacao: "",
};

const formatarData = (valor) => {
  if (!valor) return "Nao informado";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return valor;
  return data.toLocaleDateString("pt-BR");
};

const AssinaturaDigitalCertificado = () => {
  const navigate = useNavigate();
  const { IDOperador, nome, operador } = useOperador();
  const [certificadoAtual, setCertificadoAtual] = useState(null);
  const [form, setForm] = useState(formInicial);
  const [loading, setLoading] = useState(false);
  const podeAdministrar = operadorEhAdminOuGerente(operador);

  const carregarCertificado = async () => {
    try {
      setLoading(true);
      const response = await assinaturaDigitalService.buscarCertificado();
      setCertificadoAtual(response);
    } catch (error) {
      console.error("Erro ao carregar certificado ICP-Brasil.", error);
      Swal.fire("Erro", "Nao foi possivel carregar o certificado.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeAdministrar) {
      navigate("/Menu");
      return;
    }

    carregarCertificado();
  }, [podeAdministrar, navigate]);

  const atualizarCampo = (campo, valor) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const salvar = async () => {
    if (!form.certificado) {
      Swal.fire("Atencao", "Selecione o arquivo do certificado.", "warning");
      return;
    }

    if (!form.senha) {
      Swal.fire("Atencao", "Informe a senha do certificado.", "warning");
      return;
    }

    try {
      setLoading(true);
      await assinaturaDigitalService.salvarCertificado(form);
      setForm(formInicial);
      await carregarCertificado();
      Swal.fire("Sucesso", "Certificado atualizado com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao salvar certificado ICP-Brasil.", error);
      Swal.fire(
        "Erro",
        error.response?.data?.errors?.default ||
          "Nao foi possivel salvar o certificado.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const desativar = async (certificado) => {
    const confirmacao = await Swal.fire({
      title: "Desativar certificado A1?",
      text: `O certificado ${certificado.certificadoTitular || certificado.IDAssinaturaDigitalCertificado} deixara de aparecer nas assinaturas futuras.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Desativar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed) return;

    try {
      setLoading(true);
      await assinaturaDigitalService.desativarCertificado(
        certificado.IDAssinaturaDigitalCertificado,
      );
      await carregarCertificado();
      Swal.fire("Sucesso", "Certificado desativado.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.errors?.default ||
        "Nao foi possivel desativar o certificado.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="menu-container-menu certificado-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell certificado-shell">
          <MenuSuperior userId={String(IDOperador).padStart(6, "0")} userName={nome} />

          <section className="certificado-header">
            <div>
              <span><KeyRound size={17} /> ICP-Brasil</span>
              <h1>Certificado ICP-Brasil</h1>
            </div>
          </section>

          <section className="certificado-security">
            <ShieldCheck size={30} />
            <div>
              <h2>Seu certificado esta protegido.</h2>
              <p>
                Por seguranca, a senha e o arquivo do certificado nao ficam
                visiveis para os usuarios do sistema.
              </p>
              <small>
                Somente administradores e gerentes autorizados podem cadastrar ou desativar
                certificados A1.
              </small>
            </div>
          </section>

          <section className="certificado-grid">
            <article className="certificado-status">
              <header>
                <h2>Certificados A1 ativos</h2>
                <span className={certificadoAtual?.configurado ? "is-ok" : "is-pending"}>
                  {certificadoAtual?.certificados?.length || 0} ativo(s)
                </span>
              </header>

              {loading && <p>Carregando certificado...</p>}

              {!loading && certificadoAtual?.certificados?.length > 0 && (
                <dl>
                  {certificadoAtual.certificados.map((certificado) => (
                    <div key={certificado.IDAssinaturaDigitalCertificado}>
                      <dt>A1 #{certificado.IDAssinaturaDigitalCertificado}</dt>
                      <dd>{certificado.certificadoTitular || "Titular nao informado"}</dd>
                      <small>CNPJ: {certificado.certificadoCnpj || "Nao informado"}</small>
                      <small>Serial: {certificado.certificadoSerial || "Nao informado"}</small>
                      <small>Validade: {formatarData(certificado.validadeCertificado)}</small>
                      <small>Cadastrado em: {formatarData(certificado.dataCadastro)}</small>
                      <button type="button" onClick={() => desativar(certificado)}>
                        Desativar certificado
                      </button>
                    </div>
                  ))}
                </dl>
              )}

              {!loading && !certificadoAtual?.configurado && (
                <div className="certificado-empty">
                  <AlertTriangle size={22} />
                  <span>Nenhum certificado cadastrado para assinatura digital.</span>
                </div>
              )}
            </article>

            <article className="certificado-form">
              <header>
                <h2>Cadastrar certificado A1</h2>
                <p>Adicione outro certificado .pfx ou .p12 sem substituir os ativos.</p>
              </header>

              <label className="certificado-file">
                <UploadCloud size={24} />
                <span>
                  {form.certificado?.name || "Selecionar certificado .pfx ou .p12"}
                </span>
                <input
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(event) =>
                    atualizarCampo("certificado", event.target.files?.[0] || null)
                  }
                />
              </label>

              <label>
                Senha do certificado
                <input
                  type="password"
                  value={form.senha}
                  onChange={(event) => atualizarCampo("senha", event.target.value)}
                  autoComplete="new-password"
                />
              </label>

              <div className="certificado-form__columns">
                <label>
                  Titular
                  <input
                    value={form.certificadoTitular}
                    onChange={(event) =>
                      atualizarCampo("certificadoTitular", event.target.value)
                    }
                    placeholder="Pentagrama..."
                  />
                </label>
                <label>
                  CNPJ
                  <input
                    value={form.certificadoCnpj}
                    onChange={(event) =>
                      atualizarCampo("certificadoCnpj", event.target.value)
                    }
                  />
                </label>
              </div>

              <div className="certificado-form__columns">
                <label>
                  Serial
                  <input
                    value={form.certificadoSerial}
                    onChange={(event) =>
                      atualizarCampo("certificadoSerial", event.target.value)
                    }
                  />
                </label>
                <label>
                  Validade
                  <input
                    type="date"
                    value={form.validadeCertificado}
                    onChange={(event) =>
                      atualizarCampo("validadeCertificado", event.target.value)
                    }
                  />
                </label>
              </div>

              <label>
                Observacao
                <textarea
                  rows={3}
                  value={form.observacao}
                  onChange={(event) =>
                    atualizarCampo("observacao", event.target.value)
                  }
                />
              </label>

              <footer>
                <button type="button" onClick={salvar} disabled={loading}>
                  <Save size={17} />
                  Salvar certificado
                </button>
              </footer>
            </article>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AssinaturaDigitalCertificado;
